/**
 * 文件上传相关工具函数
 * 参照 galaxy-admin 
 */

import BMF from "browser-md5-file";
import OSS from "ali-oss";
import api from "@/api";
import store from "@/store";

// 对齐galaxy：定义OSSApiKey常量
const OSSApiKey = {
  Common: "common",
  Message: "message"
};

/**
 * 获取文件的 MD5 值
 * @param {File} file - 文件对象
 * @returns {Promise<string>} MD5 值
 */
export function getMd5(file) {
  return new Promise((resolve, reject) => {
    const bmf = new BMF();
    bmf.md5(file, (err, md5) => {
      if (err) {
        reject(err);
      } else {
        resolve(md5);
      }
    });
  });
}

/**
 * 自动转换文件大小单位
 * @param {number} value - 数值
 * @param {string} fromUnit - 源单位: "B" | "KB" | "MB" | "GB" | "TB"
 * @param {string} toUnit - 目标单位: "B" | "KB" | "MB" | "GB" | "TB"
 * @param {boolean} useDecimal - 是否使用十进制（1000）还是二进制（1024）
 * @returns {number} 转换后的数值
 */
export function autoConvertSize(
  value,
  fromUnit = "B",
  toUnit = "MB",
  useDecimal = false
) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  const base = useDecimal ? 1000 : 1024;

  let fromIndex = units.indexOf(fromUnit.toUpperCase());
  let toIndex = units.indexOf(toUnit.toUpperCase());

  if (fromIndex === -1 || toIndex === -1) throw new Error("单位错误");

  let bytes = value * Math.pow(base, fromIndex);
  let result = bytes / Math.pow(base, toIndex);

  return parseFloat(result.toFixed(2));
}

/**
 * 检查文件名是否重复，如果重复则自动添加序号
 * @param {Array} arr - 文件数组
 * @param {string} name - 文件名
 * @param {number} fileNameIndex - 文件名序号
 * @returns {string} 处理后的文件名
 */
export function hasSameName(arr, name, fileNameIndex = 0) {
  try {
    const originalName = name.substring(0, name.lastIndexOf("."));
    const format = name.split(".").pop();

    let tmpName = originalName;
    if (fileNameIndex > 0) {
      tmpName = `${originalName}(${fileNameIndex})`;
    }

    const newName = `${tmpName}.${format}`;
    if (arr.some((item) => item.name === newName)) {
      return hasSameName(arr, name, fileNameIndex + 1);
    } else {
      return newName;
    }
  } catch (error) {
    return name;
  }
}

/**
 * 价格单位转换
 * @param {Object} data - 价格数据对象
 * @param {string} type - 转换类型: "get" - 分转元, "update" - 元转分, "empty" - 清空
 */
export function updatePrices(data, type) {
  if (!data) return;
  // 价格单位转换 update转换为分，get转换为元，empty清空
  Object.keys(data).forEach((key) => {
    if (key.startsWith("price")) {
      const currentPrice = data[key];
      if (type === "update") {
        data[key] =
          !currentPrice && currentPrice !== 0 ? null : currentPrice * 100;
      } else if (type === "get") {
        data[key] =
          !currentPrice && currentPrice !== 0 ? null : currentPrice / 100;
      }
    }
  });
}

/**
 * 检查并更新多语言数据
 * @param {Object} data - 需要处理的数据对象
 * @param {Array} fields - 需要处理的字段配置数组
 * @param {string} type - 处理类型：'add' - 补充缺少的语言数据，'del' - 删除内容为空的语言数据
 */
export function checkLanguageData(data, fields, type) {
  const languages = ["zh_CN", "zh_TW", "en"];

  fields.forEach(({ field, nestedField, skip = false, isArrayValue = false }) => {
    if (skip) return;

    const fieldValue = data[field];
    if (!fieldValue) return;

    if (nestedField) {
      // 处理带有嵌套字段的数组
      fieldValue.forEach((item) => {
        const nestedValue = item[nestedField] || [];
        processLanguageArray(nestedValue, type, languages, isArrayValue);
      });
    } else {
      // 处理普通数组
      processLanguageArray(fieldValue, type, languages, isArrayValue);
    }
  });
}

/**
 * 处理语言数组
 * @param {Array} array - 语言数组
 * @param {string} type - 处理类型
 * @param {Array} languages - 语言列表
 * @param {boolean} isArrayValue - 值是否为数组类型
 */
function processLanguageArray(array, type, languages, isArrayValue = false) {
  if (type === "add") {
    languages.forEach((lang) => {
      if (!array.some((item) => item.locale === lang)) {
        array.push({ locale: lang, value: isArrayValue ? [] : "" });
      }
    });
  } else if (type === "del") {
    const filteredArray = array.filter((item) =>
      isArrayValue ? item.value.length : item.value
    );
    array.length = 0;
    array.push(...filteredArray);
  }
}

/**
 * 上传文件到 OSS
 * @param {Object} params - 上传参数
 * @param {File} params.file - 文件对象
 * @param {string} params.dir - 目录路径，如 "res_sticker/attachments/"
 * @param {string} params.bucket - bucket 名称（可选，不传则从 store 中获取）
 * @param {string} params.ossApiKey - OSS API Key，默认为 "common"（可选，不传则从 store 中获取）
 * @param {string} params.path - 完整路径（可选，如果传入则优先使用）
 * @returns {Promise<string>} 上传后的 URL
 */
export async function uploadFileToOss({
  file,
  dir = "",
  bucket,
  ossApiKey = OSSApiKey.Common,
  path = ""
}) {
  try {
    if (!file) {
      throw new Error("uploadFileToOss: file 不能为空");
    }

    const format = file.name ? file.name.split(".").pop() : "";
    const randomName = random_string(6) + "_" + new Date().getTime() + "." + format;

    const key = path || (dir ? `${dir}${randomName}` : randomName);

    let finalBucket = bucket;
    let finalRegion = null;

    // 从 store 获取 bucketData
    const bucketData = store.getters.bucketData || {};
    const commonBucketData = bucketData[ossApiKey] || bucketData[OSSApiKey.Common] || {};

    if (!finalBucket) {
      finalBucket = commonBucketData.bucket || "onyx-edu"; // 默认值
    }
    finalRegion = commonBucketData.region || "oss-cn-shenzhen"; // 默认值

    const url = await uploadFileToOssByPath({
      file,
      path: key,
      bucket: finalBucket,
      region: finalRegion
    });

    return url;
  } catch (error) {
    throw new Error(`上传文件失败：${error.message || error}`);
  }
}

/**
 * 按后端返回的 path 上传到 OSS（对齐 galaxy：multipartUpload(key=fileAsset.path, file)）
 * @param {Object} params
 * @param {File} params.file
 * @param {string} params.path - 后端 fileAsset.path，如 "res_card/resource/xxx.png"
 * @param {string} params.bucket - bucket（可选，不传则从 store 中获取）
 * @param {string} params.region - oss region（可选，不传则从 store 中获取）
 * @returns {Promise<string>} 上传后的 URL
 */
export async function uploadFileToOssByPath({
  file,
  path,
  bucket,
  region
}) {
  if (!file || !path) {
    throw new Error("uploadFileToOssByPath 缺少 file/path");
  }

  let finalBucket = bucket;
  let finalRegion = region;

  if (!finalBucket || !finalRegion) {
    const bucketData = store.getters.bucketData || {};
    const commonBucketData = bucketData[OSSApiKey.Common] || {};

    if (!finalBucket) {
      finalBucket = commonBucketData.bucket || "onyx-edu"; // 默认值
    }
    if (!finalRegion) {
      finalRegion = commonBucketData.region || "oss-cn-shenzhen"; // 默认值
    }
  }

  const sts = await api.aliyunSts();
  const client = new OSS({
    region: finalRegion,
    accessKeyId: sts.data.AccessKeyId,
    accessKeySecret: sts.data.AccessKeySecret,
    stsToken: sts.data.SecurityToken,
    bucket: finalBucket
  });
  const res = await client.multipartUpload(path, file);
  const url = res && res.res && res.res.requestUrls && res.res.requestUrls[0]
    ? res.res.requestUrls[0].split("?")[0]
    : "";
  return url;
}

/**
 * 生成随机字符串
 * @param {number} len - 长度
 * @returns {string} 随机字符串
 */
function random_string(len) {
  len = len || 32;
  var chars = "ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678";
  var maxPos = chars.length;
  var pwd = "";
  for (let i = 0; i < len; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
}

/**
 * 获取阿里云 STS 凭证（带缓存）
 * @param {string} bucket - bucket 名称
 * @returns {Promise<Object>} STS 凭证
 */
async function getAliyunSts(bucket) {
  // 简单的内存缓存，避免频繁请求
  const cacheKey = `sts_${bucket}`;
  const cache = getAliyunSts.cache || (getAliyunSts.cache = {});
  const cached = cache[cacheKey];

  // 如果缓存存在且未过期（5分钟），直接返回
  if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
    return cached.data;
  }

  try {
    const sts = await api.aliyunSts();
    cache[cacheKey] = {
      data: sts.data,
      timestamp: Date.now()
    };
    return sts.data;
  } catch (error) {
    throw new Error(`获取 STS 凭证失败: ${error.message || error}`);
  }
}

/**
 * 获取 OSS 文件的签名 URL（用于预览）
 * @param {Object} params - 参数
 * @param {string} params.key - OSS key
 * @param {string} params.bucket - bucket 名称（可选，不传则从 store 中获取）
 * @param {Object} params.opts - 签名选项（可选，如 expires）
 * @returns {Promise<string>} 签名 URL
 */
export async function signatureUrl({ key, bucket, opts }) {
  if (!key) {
    throw new Error("signatureUrl: key 不能为空");
  }

  // 如果已经是完整的 URL，直接返回
  if (key.startsWith("http://") || key.startsWith("https://") || key.startsWith("blob:")) {
    return key;
  }

  const bucketData = store.getters.bucketData || {};
  const commonBucketData = bucketData[OSSApiKey.Common] || {};

  let finalBucket = bucket || commonBucketData.bucket || "onyx-edu"; // 默认值
  let finalRegion = commonBucketData.region || "oss-cn-shenzhen"; // 默认值

  const creds = await getAliyunSts(finalBucket);

  if (!creds || !creds.AccessKeyId || !creds.AccessKeySecret || !creds.SecurityToken) {
    throw new Error(`获取 STS 凭证失败: 凭证信息不完整 (bucket: ${finalBucket})`);
  }

  const client = new OSS({
    region: finalRegion,
    accessKeyId: creds.AccessKeyId,
    accessKeySecret: creds.AccessKeySecret,
    stsToken: creds.SecurityToken,
    bucket: finalBucket,
    // 对齐 galaxy-admin：添加自动刷新 STS token 配置
    refreshSTSTokenInterval: 1000 * 60 * 10, // 10 分钟刷新一次
    refreshSTSToken: async () => {
      const newCreds = await getAliyunSts(finalBucket);
      return {
        bucket: finalBucket,
        region: finalRegion,
        accessKeyId: newCreds.AccessKeyId,
        accessKeySecret: newCreds.AccessKeySecret,
        stsToken: newCreds.SecurityToken
      };
    }
  });

  // 生成签名 URL，有效期 1 小时（默认）
  // 对齐 galaxy-admin：直接返回，失败时抛出错误
  try {
    const url = client.signatureUrl(key, opts || { expires: 3600 });
    if (!url || (!url.startsWith("http://") && !url.startsWith("https://"))) {
      throw new Error(`生成的签名 URL 格式无效: ${url} (key: ${key}, bucket: ${finalBucket})`);
    }
    return url;
  } catch (error) {
    console.error(`生成签名 URL 失败 (key: ${key}, bucket: ${finalBucket}):`, error);
    throw new Error(`生成签名 URL 失败: ${error.message || error}`);
  }
}

